import React, { Component } from 'react';
import {
  FaRegWindowMaximize,
  FaRegWindowRestore,
  FaRoute,
} from 'react-icons/all';
import { Loader } from '../../../components/Loader/Loader';
import { OrderInstruction } from '../../../components/maps/BundleMap';
import { DirectionsData, fetchDirections } from '../../../fetch/mapboxApi';
import { CourierJson } from '../../../types/bundleTypes';
import { Region } from '../../../types/orderTypes';
import { classNames } from '../../../utils/classUtils';
import { getBundleColor, getBundleFontColor } from '../utils/bundleDraft';
import { BundleMapboxMap } from '../../planner/bundleMap/BundleMapboxMap';
import './BundleMapWindow.scss';

interface Props {
  orderInstructions: OrderInstruction[];
  bundleId: string;
  region: Region;
  courier?: CourierJson;
  onClose: () => void;
  onTop: boolean;
  onSetFocus?: () => void;
  attachToBundlePlate?: boolean;
  newMonitor?: boolean;
  refreshZoomPosition?: boolean;
}

interface State {
  routeDuration?: number;
  routeDistance?: number;

  isDragging: boolean;
  originalX: number;
  originalY: number;
  translateX: number;
  translateY: number;
  lastTranslateX: number;
  lastTranslateY: number;
  maximized: boolean;
  animateMarkers: boolean;
  directions: DirectionsData | undefined;
  loadingDirections: boolean;
}

export class BundleMapWindow extends Component<Props, State> {
  private dragElement: HTMLDivElement;
  private mapElement: HTMLDivElement;

  constructor(props) {
    super(props);

    this.state = {
      isDragging: false,
      originalX: 0,
      originalY: 0,
      translateX: 0,
      translateY: 0,
      lastTranslateX: 0,
      lastTranslateY: 0,
      maximized: false,
      animateMarkers: false,
      directions: undefined,
      loadingDirections: true,
    };
    this.onKeyPressed = this.onKeyPressed.bind(this);
  }

  componentDidMount = () => {
    document.addEventListener('keydown', this.onKeyPressed, false);
    document.addEventListener('mousedown', this.handleMouseDown, false);

    const deliveryInstructions = this.props.orderInstructions.filter(
      instruction => instruction.instruction.type === 'DELIVERY'
    );
    // We assume that every order have the same pickup address
    const pickupCoordinate: [number, number] = [
      deliveryInstructions[0].order.originAddress.location.longitude,
      deliveryInstructions[0].order.originAddress.location.latitude,
    ];
    const deliveryCoordinates: [number, number][] = deliveryInstructions.map(
      instruction => [
        instruction.order.destinationAddress.location.longitude,
        instruction.order.destinationAddress.location.latitude,
      ]
    );

    fetchDirections(
      this.props.courier?.travelMode === 'BIKE' ? 'cycling' : 'driving',
      [pickupCoordinate, ...deliveryCoordinates]
    )
      .then(data =>
        this.setState({
          directions: data,
          routeDistance: data?.distance ?? undefined,
          routeDuration:
            data?.duration / 60 ? Math.ceil(data.duration / 60) : undefined,
        })
      )
      .finally(() => {
        this.setState({
          loadingDirections: false,
        });
      });
  };

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeyPressed, false);

    document.removeEventListener('mousemove', this.handleMouseMove, false);
    document.removeEventListener('mouseup', this.handleMouseUp, false);
    document.removeEventListener('mousedown', this.handleMouseDown, false);
  }

  handleMouseDown = e => {
    if (
      this.state.maximized ||
      !this.dragElement.contains(e.target) ||
      this.mapElement.contains(e.target)
    ) {
      return;
    }

    e.stopPropagation();

    document.addEventListener('mousemove', this.handleMouseMove, false);
    document.addEventListener('mouseup', this.handleMouseUp, false);

    this.setFocus();

    this.setState({
      originalX: e.clientX,
      originalY: e.clientY,
      isDragging: true,
    });
  };

  handleMouseMove = ({ clientX, clientY }) => {
    if (!this.state.isDragging) {
      return;
    }

    this.setState(prevState => ({
      translateX: clientX - prevState.originalX + prevState.lastTranslateX,
      translateY: clientY - prevState.originalY + prevState.lastTranslateY,
    }));
  };

  handleMouseUp = e => {
    document.removeEventListener('mousemove', this.handleMouseMove, false);
    document.removeEventListener('mouseup', this.handleMouseUp, false);

    e.stopPropagation();

    this.setState(
      {
        originalX: 0,
        originalY: 0,
        lastTranslateX: this.state.translateX,
        lastTranslateY: this.state.translateY,

        isDragging: false,
      },
      () => {
        this.setFocus();
      }
    );
  };

  setFocus() {
    if (!this.props.onTop && this.props.onSetFocus) {
      this.props.onSetFocus();
    }
  }

  onKeyPressed(event) {
    if (event.keyCode === 27) {
      if (this.state.maximized) {
        this.setState({ maximized: false });
      } else {
        this.props.onClose();
      }
    }
  }

  render() {
    const {
      orderInstructions,
      bundleId,
      onTop,
      courier,
      attachToBundlePlate,
      newMonitor,
    } = this.props;
    const { isDragging, maximized } = this.state;
    const x = this.state.translateX;
    const y = this.state.translateY;
    return (
      <div
        className={classNames(
          {
            'planner-bundle-map__dragging': isDragging,
            'planner-bundle-map__focused': onTop,
            'planner-bundle-map__maximized': maximized,
            'planner-bundle-map__focused-monitor-plate': attachToBundlePlate,
            'planner-bundle-map__new-monitor': newMonitor,
          },
          'planner-bundle-map'
        )}
        style={{
          transform: maximized ? undefined : `translate(${x}px, ${y}px)`,
        }}
        onClick={e => {
          e.stopPropagation();
          this.setFocus();
        }}
        ref={node => {
          this.dragElement = node;
        }}
      >
        <div className="planner-bundle-map--header">
          <div
            className="planner-bundle-map--maximize"
            onClick={event => {
              event.stopPropagation();
              this.setState({ maximized: !this.state.maximized });
            }}
          >
            {maximized ? <FaRegWindowRestore /> : <FaRegWindowMaximize />}
          </div>
          <div
            className="planner-bundle-map--header__title"
            style={{
              backgroundColor: getBundleColor(bundleId),
              color: getBundleFontColor(bundleId),
            }}
          >
            Bundle {bundleId}
          </div>
          <div className="planner-bundle-map--route">
            {this.state.routeDuration && (
              <>
                <span>
                  {this.state.routeDuration} minutes /{' '}
                  {(this.state.routeDistance / 1000).toFixed(1)} km
                </span>
                <div
                  className="planner-bundle-map--route-animate"
                  onClick={event => {
                    event.stopPropagation();
                    this.setState({
                      animateMarkers: !this.state.animateMarkers,
                    });
                  }}
                >
                  <FaRoute />
                </div>
              </>
            )}
          </div>
          <div
            className="planner-bundle-map--close"
            onClick={event => {
              event.stopPropagation();
              this.props.onClose();
            }}
          >
            Close
          </div>
        </div>
        <div
          ref={node => {
            this.mapElement = node;
          }}
        >
          {this.state.loadingDirections && (
            <div className="planner-bundle-map__loading">
              <Loader />
              <span>Loading directions</span>
            </div>
          )}
          {this.state.directions && (
            <BundleMapboxMap
              region={this.props.region}
              orderInstructions={orderInstructions}
              directions={this.state.directions}
              courier={courier}
              height={maximized ? 'calc(80vh - 60px)' : 'calc(50vh - 60px)'}
            />
          )}
        </div>
        <div className="planner-bundle-map--help">
          {!maximized && 'Click on the border to drag the map window.'}
        </div>
      </div>
    );
  }
}
